// JSON-LD schema builders — ONLY uses facts already present in the project.
// No fabricated license numbers, years in business, insurance claims, warranties,
// reviews, prices, or awards.
import {
  CLEANING_CANONICAL_ORIGIN,
  LEES_CANONICAL_ORIGIN,
} from "./hosts";

// ----- Verified facts (sourced from existing codebase/content) -----
const LEES = {
  name: "Lee's Heating & Cooling",
  phone: "+1-219-455-1453",
  email: "LeesHeatingAndCoolingNWI@gmail.com",
  url: LEES_CANONICAL_ORIGIN,
  logo: `${LEES_CANONICAL_ORIGIN}/img/lees-logo-300.png`,
  // Service area — primary + strong surrounding (listed on /hvac)
  areaServed: [
    "Lake Station, IN", "Hobart, IN", "Gary, IN", "Portage, IN", "Merrillville, IN",
    "Hammond, IN", "Highland, IN", "Griffith, IN", "Munster, IN", "Crown Point, IN",
    "Schererville, IN", "Dyer, IN", "St. John, IN", "Chesterton, IN", "South Haven, IN",
    "Valparaiso, IN", "Whiting, IN", "East Chicago, IN", "Cedar Lake, IN",
    "Lowell, IN", "La Porte, IN", "Northwest Indiana",
  ],
  services: [
    "Emergency HVAC",
    "AC Repair",
    "Furnace Repair",
    "HVAC Maintenance & Tune-Up",
    "Mini-Split Service & Installation",
    "Refrigeration",
    "Light Commercial HVAC",
    "HVAC Diagnostics",
    "HVAC Second Opinions",
  ],
  // Facts present in /legacy content
  founderCredentials: [
    "EPA Universal Certified",
    "Associate of Applied Science in HVAC — Ivy Tech Community College",
  ],
  slogan: "Honest HVAC Work for Northwest Indiana Homes and Businesses",
};

const CLEANING = {
  name: "S.S. Cleaning Solutions",
  phone: "+1-219-455-1453",
  email: "sscleaningsolutionsco@gmail.com",
  url: CLEANING_CANONICAL_ORIGIN,
  logo: `${CLEANING_CANONICAL_ORIGIN}/img/ss-logo-512.png`,
};

// ----- Lee's HVACBusiness schema -----
export function leesBusinessSchema(opts = {}) {
  const sameAs = (opts.sameAs || []).filter(Boolean);
  const schema = {
    "@context": "https://schema.org",
    "@type": ["HVACBusiness", "LocalBusiness"],
    "@id": `${LEES.url}#business`,
    name: LEES.name,
    legalName: LEES.name,
    url: LEES.url,
    image: LEES.logo,
    logo: LEES.logo,
    telephone: LEES.phone,
    email: LEES.email,
    slogan: LEES.slogan,
    description: "Family-owned HVAC company in Northwest Indiana carrying on a three-generation family legacy. Diagnostic-first AC, furnace, maintenance, second-opinion and emergency HVAC service (emergency availability subject to technician availability).",
    address: {
      "@type": "PostalAddress",
      addressRegion: "IN",
      addressCountry: "US",
    },
    areaServed: LEES.areaServed.map((n) => ({ "@type": "City", name: n })),
    knowsAbout: LEES.services,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "HVAC Services",
      itemListElement: LEES.services.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s },
      })),
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00", closes: "18:00",
      },
    ],
  };
  if (sameAs.length) schema.sameAs = sameAs;
  return schema;
}

// Individual Service schemas (one per core HVAC offering)
export function leesServiceSchemas() {
  const base = {
    "@context": "https://schema.org",
    "@type": "Service",
    provider: { "@id": `${LEES.url}#business` },
    areaServed: LEES.areaServed.map((n) => ({ "@type": "City", name: n })),
  };
  return LEES.services.map((s) => ({ ...base, name: s, serviceType: s }));
}

// Breadcrumbs
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

// FAQPage — pass array of {question, answer}; only facts you own.
export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

// ----- SS Cleaning schema (owner-approved facts only) -----
const CLEANING_AREAS = [
  "Lake Station", "Hobart", "Gary", "Portage", "Merrillville", "Hammond",
  "Highland", "Griffith", "Munster", "Crown Point", "Schererville", "Dyer", "St. John",
  "Chesterton", "South Haven", "Valparaiso", "Whiting", "East Chicago", "Cedar Lake",
  "Lowell", "La Porte",
].map((c) => `${c}, IN`).concat("Northwest Indiana");

const CLEANING_SERVICES = [
  "House Cleaning", "Deep Cleaning", "Recurring Cleaning", "Move-In / Move-Out Cleaning",
  "Real-Estate & Rental Turnover Cleaning", "Commercial Cleaning", "Office Cleaning",
  "Church Cleaning", "Junk & Debris Removal", "Property Cleanup",
];

export function cleaningBusinessSchema(opts = {}) {
  const sameAs = (opts.sameAs || []).filter(Boolean);
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${CLEANING.url}#business`,
    name: CLEANING.name,
    url: CLEANING.url,
    image: CLEANING.logo,
    logo: CLEANING.logo,
    telephone: CLEANING.phone,
    email: CLEANING.email,
    slogan: "Detail-oriented cleaning without cutting corners.",
    description: "Locally family-owned and woman-owned residential and commercial cleaning company serving Northwest Indiana.",
    founder: { "@type": "Person", name: "Stephanie Spiehler", jobTitle: "Owner/Operator" },
    address: { "@type": "PostalAddress", addressRegion: "IN", addressCountry: "US" },
    areaServed: CLEANING_AREAS.map((n) => ({ "@type": "City", name: n })),
    knowsAbout: CLEANING_SERVICES,
    paymentAccepted: "Cash, Credit Card, Debit Card, Venmo, PayPal, Chime",
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "07:00", closes: "19:00",
    }],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Cleaning Services",
      itemListElement: CLEANING_SERVICES.map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s } })),
    },
  };
  if (sameAs.length) schema.sameAs = sameAs;
  return schema;
}

function serviceSchema(brand, areas, { name, description, url }) {
  const s = {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    serviceType: name,
    provider: { "@id": `${brand.url}#business` },
    areaServed: areas.map((n) => ({ "@type": "City", name: n })),
  };
  if (description) s.description = description;
  if (url) s.url = url;
  return s;
}

export const cleaningServiceSchema = (opts) => serviceSchema(CLEANING, CLEANING_AREAS, opts);
export const leesServiceSchema = (opts) => serviceSchema(LEES, LEES.areaServed, opts);

export const BRAND_FACTS = { LEES, CLEANING };
